import { showToast } from '../../utils/toast.js';
import { CDN_URL, catalogJsonUrl } from '../../../js/config.js';
import { normalizeGender } from '../../utils/genderUtils.js';
import fetchCache from '../../utils/fetchCache.js';
import {
    loadCreditPricing,
    computeOutfitBatchTotal,
    buildLocalCreditResult,
} from '../creditCostApi.js';
import { getCharacterFormStyle } from '../../utils/userPrefs.js';
import { perkTierForUser } from '../../utils/membershipUsage.js';
import { validation } from '../utils/validation.js';
import { $, $$, on, addClass, removeClass, val, hide, html } from '../../../core/utils/dom.js';
import { commitCharacterNameChange, createKvRow, initStep2KvTable, refreshKvCounter, releaseKvRows, rememberCharacterName, setKvValue } from '../utils/kvRow.js';
import { initializeNegativePromptAutocomplete, initializeOutfitPromptAutocomplete } from '../promptAutocomplete.js';
import {
    DEFAULT_BODY_OPTION_ID,
    BODY_OPTION_CATALOG,
    BODY_PROMPT_TAG_TOKEN_KEYS,
    BUST_KEYWORDS,
    FEMALE_BODY_KEYWORDS,
    MALE_BODY_KEYWORDS,
    resolveBodyOptionId,
    getBodyOptionById,
    splitPromptTagsAware,
    FEMALE_BODY_TYPE_WARNING_TAGS,
    MALE_BODY_TYPE_WARNING_TAGS,
    normalizeBodyPromptTokenKeys,
} from '../bodyOptionCatalog.js';
import { initCharacterAppearanceHelpers } from '../utils/standalonePromptHelpers.js';
import { openPromptImagePicker } from '../ui/promptImagePickerModal.js';
import { storage } from '../../utils/storage.js';
import { syncSpriteCredits } from '../videoScheduleApi.js';
import { savePreview, loadPreview, deletePreview, cleanupAndSavePreview } from '../../utils/previewCache.js';
import { getBase64FromUrl } from '../imageGeneration.js';
import { findPremiumAppearanceTag } from '../danbooruTags.js';
import { stripCharacterGenderTags } from '../characterGenderTag.js';
import { filterOutfitsForSfw, outfitIsHiddenInFilteredMode } from '../../utils/nsfwTags.js';

const BODY_ASSET_BASE_PATH = './assets/images/body';
const BODY_OPTIONS = BODY_OPTION_CATALOG;
const BODY_PROMPT_TAGS = Object.values(BODY_OPTION_CATALOG).flatMap((options) =>
    options.map((option) => option.promptTag)
);

let selectedBodyOptionId = DEFAULT_BODY_OPTION_ID;
let currentBodyGender = 'female';

const ALL_BODY_KEYWORDS = [...FEMALE_BODY_KEYWORDS, ...MALE_BODY_KEYWORDS];

const getBodyImagePath = (gender, optionId) => {
    const normalizedGender = normalizeGender(gender);
    const normalizedId = resolveBodyOptionId(normalizedGender, optionId);
    if (normalizedGender === 'female') {
        const style = getCharacterFormStyle();
        return `${BODY_ASSET_BASE_PATH}/female/${style}/${normalizedId}.webp`;
    }
    return `${BODY_ASSET_BASE_PATH}/${normalizedGender}/${normalizedId}.webp`;
};

const getBustInput = () => document.getElementById('ai-character-bust');

const canSeePremiumBodyOptions = () => {
    const membershipTier = perkTierForUser(storage.get('userData') || {});
    return Boolean(membershipTier && membershipTier !== 'free');
};

const getVisibleBodyOptions = (gender) => {
    const options = (BODY_OPTIONS[gender] || []).filter((option) => !option.hiddenFromPicker);
    if (canSeePremiumBodyOptions()) return options;
    return options.filter((option) => !findPremiumAppearanceTag(option.promptTag || ''));
};

const isBustInputEmpty = (hiddenInput) => {
    if (!hiddenInput) return true;
    return String(hiddenInput.value ?? '').trim() === '';
};

const syncBodySelectionFromInput = () => {
    const hiddenInput = getBustInput();
    if (!hiddenInput) return;
    if (isBustInputEmpty(hiddenInput)) {
        if (selectedBodyOptionId !== null) clearBodySelectionState();
        return;
    }
    const normalizedId = resolveBodyOptionId(currentBodyGender, hiddenInput.value);
    if (normalizedId === selectedBodyOptionId) return;
    setBodySelectionState(normalizedId, { gender: currentBodyGender, silent: true });
};

/** Clear body selection without mutating the appearance prompt (catalog / exact-twin path). */
const clearBodySelectionState = () => {
    const hiddenInput = getBustInput();
    if (!hiddenInput) return;
    selectedBodyOptionId = null;
    hiddenInput.value = '';
};

const setBodySelectionState = (optionId, options = {}) => {
    const hiddenInput = getBustInput();
    if (!hiddenInput) return;
    const { silent = false, gender = currentBodyGender } = options;
    const normalizedGender = normalizeGender(gender);
    const normalizedId = resolveBodyOptionId(normalizedGender, optionId);
    const previousGender = currentBodyGender;
    const previousId = selectedBodyOptionId;

    if (
        previousId !== null &&
        (previousId !== normalizedId || previousGender !== normalizedGender)
    ) {
        const oldOption = getBodyOptionById(previousGender, previousId);
        if (oldOption) {
            removeBodyPromptTag(oldOption.promptTag);
        }
    }

    selectedBodyOptionId = normalizedId;
    currentBodyGender = normalizedGender;
    hiddenInput.value = String(normalizedId);

    if (!silent) {
        updateBodyPromptTags();
    }
};

const removeBodyPromptTag = (promptTag) => {
    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!promptTextarea || !promptTag) return;
    const currentPrompt = promptTextarea.value;
    const removedTokenKeys = new Set(splitPromptTags(promptTag).map(normalizePromptTagKey));
    const sections = extractPromptSections(currentPrompt);
    const remainingBodyTags = sections.bodyTokens.filter((tag) => !removedTokenKeys.has(normalizePromptTagKey(tag)));
    const nextPrompt = buildOrderedCharacterPrompt(currentPrompt, currentBodyGender, remainingBodyTags);

    if (nextPrompt !== currentPrompt) {
        promptTextarea.value = nextPrompt;
        promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

const initializeBodyGrid = (gender = 'female') => {
    const hiddenInput = getBustInput();
    if (!hiddenInput) return;
    const normalizedGender = normalizeGender(gender);
    currentBodyGender = normalizedGender;

    if (isBustInputEmpty(hiddenInput)) {
        clearBodySelectionState();
    } else {
        const normalizedId = resolveBodyOptionId(normalizedGender, hiddenInput.value);
        setBodySelectionState(normalizedId, { gender: normalizedGender, silent: true });
    }
};

// Bust and body type mapping functions (moved from Cloudflare worker)
const getBustTags = (bust, gender = currentBodyGender) => {
    if (bust === null || bust === undefined || bust === '') return '';
    const option = getBodyOptionById(gender, bust);
    return option ? option.promptTag : '';
};

// Global flag to prevent automatic prompt appending during AI assistant operations
window.isAIAssistantRunning = window.isAIAssistantRunning || false;

const removeAllAutoGeneratedTags = (promptText) => {
    let cleanedPrompt = promptText;

    // Remove gender tokens anywhere (AI may embed them mid-prompt, not just at the beginning)
    // Examples to remove: "1girl", "1boy," ", 1girl," "\n1boy"
    cleanedPrompt = cleanedPrompt
        .replace(/(?:^|[\n,])\s*1(boy|girl)\s*(?:,)?\s*/gi, (m) => (m.startsWith('\n') ? '\n' : ' '));

    [...BUST_KEYWORDS, ...ALL_BODY_KEYWORDS].forEach(keyword => {
        const patterns = [
            new RegExp(`\\[${keyword.replace(/\s+/g, '\\s+')}[:\\w\\s]+\\]`, 'gi'), // [keyword:other] pattern
            new RegExp(`\\(${keyword.replace(/\s+/g, '\\s+')}:[\\d.]+\\)`, 'gi'),   // (keyword:0.8) pattern
            new RegExp(`\\b${keyword.replace(/\s+/g, '\\s+')}\\b`, 'gi')            // plain keyword
        ];

        patterns.forEach(pattern => {
            cleanedPrompt = cleanedPrompt.replace(pattern, '');
        });
    });

    /**
     * BODY_PROMPT_TAGS contain multi-token comma-separated sequences (e.g. "petite, large breasts,")
     * and weighted tags (e.g. "(toned:0.6), compact abs"). Word-boundary matching fails on commas.
     * Remove them as full comma/newline-delimited segments, tolerating whitespace differences.
     */
    const buildBodyPromptTagPattern = (rawTag) => {
        const core = String(rawTag || '').trim().replace(/,+\s*$/, '');
        if (!core) return null;

        // Escape regex specials, then make commas whitespace-tolerant.
        const escapedCore = core
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            .replace(/,/g, '\\s*,\\s*');

        // Match tag as a whole segment delimited by start/comma/newline and followed by comma/newline/end.
        return new RegExp(`(?:^|[\\n,])\\s*${escapedCore}\\s*(?:,\\s*)?(?=\\n|,|$)`, 'gi');
    };

    BODY_PROMPT_TAGS.forEach((tag) => {
        const pattern = buildBodyPromptTagPattern(tag);
        if (!pattern) return;
        cleanedPrompt = cleanedPrompt.replace(pattern, '');
    });

    cleanedPrompt = cleanedPrompt
        .replace(/,\s*,/g, ',') // Remove double commas
        .replace(/,\s*$/, '') // Remove trailing comma
        .replace(/^\s*,/, '') // Remove leading comma
        .replace(/\n\s*\n/g, '\n') // Remove empty lines
        .replace(/\n\s*$/, '') // Remove trailing newline with spaces
        .replace(/^\s*\n/, '') // Remove leading newline with spaces
        .replace(/[ \t]+/g, ' ') // Normalize spaces (but preserve newlines)
        .trim();

    return cleanedPrompt;
};

const organizeAutoGeneratedTags = (promptText, gender = 'female') => {
    const lines = promptText.split('\n');
    let userPrompt = '';
    let genderPrefix = '';
    let bustTags = '';
    let bodyTypeTags = '';

    const bodyKeywords = gender === 'male' ? MALE_BODY_KEYWORDS : FEMALE_BODY_KEYWORDS;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const genderMatch = line.match(/^1(boy|girl)(?:\s*,\s*(.*))?$/i);
        if (genderMatch) {
            genderPrefix = genderMatch[1] === 'boy' ? '1boy' : '1girl';
            if (genderMatch[2]) {
                const remainingTags = splitPromptTags(genderMatch[2]);
                remainingTags.forEach(tag => {
                    const tagLower = tag.toLowerCase();
                    const cleanTag = tag.replace(/[():\d.]/g, '').trim().toLowerCase();

                    if (BUST_KEYWORDS.some(keyword => cleanTag.includes(keyword.toLowerCase()))) {
                        if (bustTags) bustTags += ', ';
                        bustTags += tag;
                    } else if (bodyKeywords.some(keyword => cleanTag.includes(keyword.toLowerCase()))) {
                        if (bodyTypeTags) bodyTypeTags += ', ';
                        bodyTypeTags += tag;
                    } else {
                        if (userPrompt) userPrompt += ', ';
                        userPrompt += tag;
                    }
                });
            }
            continue;
        }

        const lineLower = line.toLowerCase();
        const hasBustTag = BUST_KEYWORDS.some(keyword => lineLower.includes(keyword.toLowerCase()));
        const hasBodyTag = bodyKeywords.some(keyword => lineLower.includes(keyword.toLowerCase()));

        if (hasBustTag || hasBodyTag) {
            const tags = splitPromptTags(line);

            tags.forEach(tag => {
                const tagLower = tag.toLowerCase();
                const cleanTag = tag.replace(/[():\d.]/g, '').trim().toLowerCase();

                if (BUST_KEYWORDS.some(keyword => cleanTag.includes(keyword.toLowerCase()))) {
                    if (bustTags) bustTags += ', ';
                    bustTags += tag;
                } else if (bodyKeywords.some(keyword => cleanTag.includes(keyword.toLowerCase()))) {
                    if (bodyTypeTags) bodyTypeTags += ', ';
                    bodyTypeTags += tag;
                } else {
                    if (userPrompt) userPrompt += ', ';
                    userPrompt += tag;
                }
            });
        } else {
            if (userPrompt) userPrompt += '\n';
            userPrompt += line;
        }
    }

    return { userPrompt: userPrompt.trim(), genderPrefix, bustTags, bodyTypeTags };
};

const rebuildPromptWithTags = (userPrompt, genderPrefix, bustTags, bodyTypeTags, gender = 'female') => {
    let result = '';

    if (genderPrefix) {
        result = genderPrefix + ',';
    }

    if (userPrompt) {
        const separator = result ? '\n' : '';
        result += separator + userPrompt + ',';
    }

    if (bustTags) {
        const separator = result ? '\n' : '';
        result += separator + bustTags + ',';
    }

    if (bodyTypeTags) {
        const separator = result ? '\n' : '';
        result += separator + bodyTypeTags + ',';
    }

    return result;
};

const splitPromptTags = splitPromptTagsAware;

const normalizePromptTagKey = (tag) => {
    return String(tag || '').toLowerCase().replace(/\s+/g, ' ').trim();
};

const uniquePromptTags = (tags) => {
    const seen = new Set();
    const unique = [];
    tags.forEach((tag) => {
        const normalized = normalizePromptTagKey(tag);
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        unique.push(tag.trim());
    });
    return unique;
};

const extractPromptSections = (promptText) => {
    const tokens = splitPromptTags(promptText);
    const bodyTokens = [];
    const otherTokens = [];

    tokens.forEach((token) => {
        const normalizedToken = normalizePromptTagKey(token);
        if (!normalizedToken || normalizedToken === '1boy' || normalizedToken === '1girl') {
            return;
        }

        if (BODY_PROMPT_TAG_TOKEN_KEYS.has(normalizedToken)) {
            bodyTokens.push(token);
            return;
        }

        otherTokens.push(token);
    });

    return { bodyTokens, otherTokens };
};

const buildOrderedCharacterPrompt = (promptText, _gender = 'female', bodyTagsOverride = null) => {
    const { bodyTokens, otherTokens } = extractPromptSections(promptText);
    const chosenBodyTokens = bodyTagsOverride === null ? bodyTokens : splitPromptTags(bodyTagsOverride);

    const orderedTags = [
        ...uniquePromptTags(chosenBodyTokens),
        ...uniquePromptTags(otherTokens)
    ];

    return orderedTags.length ? `${orderedTags.join(', ')},` : '';
};

const updateAllPromptTags = (gender = 'female') => {
    if (window.isAIAssistantRunning) {
        return;
    }

    const bustEl = $('#ai-character-bust');
    // Empty bust = catalog/exact appearance; do not strip or re-append body tags.
    // Still drop 1boy/1girl so a gender change cannot leave the count tag in the field.
    if (isBustInputEmpty(bustEl)) {
        const promptTextarea = document.getElementById('ai-character-prompt');
        if (!promptTextarea) return;
        const stripped = stripCharacterGenderTags(promptTextarea.value);
        if (stripped !== promptTextarea.value) {
            promptTextarea.value = stripped;
            promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
        }
        return;
    }

    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!promptTextarea) return;

    const currentPrompt = promptTextarea.value;
    const cleanedPrompt = removeAllAutoGeneratedTags(currentPrompt);

    const currentBust = bustEl ? val(bustEl) : '';

    const normalizedGender = normalizeGender(gender);
    const bustTags = currentBust ? getBustTags(currentBust, normalizedGender) : '';
    const newPrompt = buildOrderedCharacterPrompt(cleanedPrompt, normalizedGender, bustTags);

    if (newPrompt !== currentPrompt) {
        promptTextarea.value = newPrompt;
        promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

const updateBodyPromptTags = () => {
    if (window.isAIAssistantRunning) {
        return;
    }

    const bustEl = $('#ai-character-bust');
    if (isBustInputEmpty(bustEl)) return;

    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!promptTextarea) return;

    // Get current body selection and add the new tag
    const currentBust = bustEl ? val(bustEl) : '';
    const newBustTag = currentBust ? getBustTags(currentBust, currentBodyGender) : '';

    if (!newBustTag) return;

    const newPrompt = buildOrderedCharacterPrompt(promptTextarea.value, currentBodyGender, newBustTag);

    if (newPrompt !== promptTextarea.value) {
        promptTextarea.value = newPrompt;
        promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

const getCharacterPromptWarningCopy = (key, fallback) => {
    const lang = (window.getCurrentLanguage && window.getCurrentLanguage()) || 'en';
    return window.translations?.[lang]?.['create-character']?.[key]
        ?? window.translations?.en?.['create-character']?.[key]
        ?? fallback;
};

const appearancePromptTokenKeys = (promptText) =>
    normalizeBodyPromptTokenKeys(promptText).map((key) => key.replace(/_/g, ' '));

const refreshCharacterPromptWarnings = () => {
    const warningEl = document.getElementById('character-prompt-warnings');
    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!warningEl || !promptTextarea) return;

    const promptText = promptTextarea.value || '';
    warningEl.replaceChildren();
    if (promptText.trim().length < 10) {
        warningEl.hidden = true;
        return;
    }

    const keys = appearancePromptTokenKeys(promptText);
    const hasToken = (needle) => keys.some((key) => key.includes(needle));
    const messages = [
        getCharacterPromptWarningCopy(
            'prompt_warn_intro',
            'For better character consistency, please consider doing the following:'
        ),
    ];

    if (!hasToken('hair')) {
        messages.push(getCharacterPromptWarningCopy(
            'prompt_warn_hair',
            'select a haircut and a hair color (example: twintails, blonde hair).'
        ));
    }
    if (!hasToken('eyes')) {
        messages.push(getCharacterPromptWarningCopy(
            'prompt_warn_eyes',
            'specify an eye color and maybe an eye shape (example: blue eyes, tareme).'
        ));
    }
    const bodyTypeTags = currentBodyGender === 'male'
        ? MALE_BODY_TYPE_WARNING_TAGS
        : FEMALE_BODY_TYPE_WARNING_TAGS;
    if (!bodyTypeTags.some((tag) => hasToken(tag))) {
        messages.push(getCharacterPromptWarningCopy(
            'prompt_warn_body',
            'Use the Body helper to set a body type.'
        ));
    }
    if (currentBodyGender === 'female' && !hasToken('breasts') && !hasToken('flat chest')) {
        messages.push(getCharacterPromptWarningCopy(
            'prompt_warn_breasts',
            'Use the Body helper to set a breast size.'
        ));
    }

    if (messages.length < 2) {
        warningEl.hidden = true;
        return;
    }

    messages.forEach((message) => {
        const line = document.createElement('span');
        line.className = 'character-prompt-warnings__line';
        line.textContent = message;
        warningEl.appendChild(line);
    });
    warningEl.hidden = false;
};

const updateCharacterPromptGenderPrefix = (gender = 'female') => {
    if (window.isAIAssistantRunning) {
        return;
    }

    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!promptTextarea) return;

    const currentPrompt = promptTextarea.value;
    const newPrompt = buildOrderedCharacterPrompt(currentPrompt, gender);

    if (newPrompt !== currentPrompt) {
        promptTextarea.value = newPrompt;
        promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

const updateCharacterPromptTags = (newTags, tagType, gender = 'female') => {
    if (!newTags) return;

    if (window.isAIAssistantRunning) {
        return;
    }

    const promptTextarea = document.getElementById('ai-character-prompt');
    if (!promptTextarea) return;

    const currentPrompt = promptTextarea.value;
    const bodyTagsOverride = (tagType === 'bust' || tagType === 'bodyType') ? newTags : null;
    const newPrompt = buildOrderedCharacterPrompt(currentPrompt, gender, bodyTagsOverride);

    if (newPrompt !== currentPrompt) {
        promptTextarea.value = newPrompt;
        promptTextarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
};

// Export functions to global window for AI assistant access
window.getBustTags = getBustTags;
window.updateCharacterPromptTags = updateCharacterPromptTags;
window.updateCharacterPromptGenderPrefix = updateCharacterPromptGenderPrefix;
window.updateAllPromptTags = updateAllPromptTags;
window.removeAllAutoGeneratedTags = removeAllAutoGeneratedTags;
window.setBodySelectionState = setBodySelectionState;
window.clearBodySelectionState = clearBodySelectionState;

// URL & Slot helpers 
const urlParams = new URLSearchParams(window.location.search);
export const slotParam = urlParams.get('slot');
export const scenarioDraftKey = slotParam ? `character_draft_from_scenario_slot${slotParam}` : null;

// Draft Persistence Helpers 
const CHAR_DRAFT_KEY = 'character_draft_v1';

const getPreviewUrl = () => {
    const div = document.querySelector('#ai-character-prompt-preview .card-front');
    if (!div) return '';
    /** @type {HTMLImageElement} */
    const img = div.querySelector('img');
    if (img?.src) return img.src;
    const bg = div.style.backgroundImage || '';
    const m = bg.match(/url\(["']?(.*?)["']?\)/);
    return m ? m[1] : '';
};

const embedOrStore = async (str) => {
    if (!str) return '';
    if (str.startsWith('data:') && str.length > 100_000) {
        try {
            const id = await savePreview(str);
            return `db:${id}`;
        } catch (e) {
            console.warn('[CharacterDraft] savePreview failed', e);
            return str;
        }
    }
    return str;
};

// ===== Character Outfits Selection =====

let selectedOutfits = new Set([0, 1]); // IDs 0 and 1 are always selected by default
let existingOutfitIds = new Set([0, 1]);
let availableOutfits = [];
/** @type {Map<number, string>} */
let outfitPromptOverrides = new Map();
let outfitNegativeOverrides = new Map();

const isExistingOutfit = (outfitId) => existingOutfitIds.has(Number(outfitId));

export const lockExistingCharacterOutfits = (ids) => {
    existingOutfitIds = new Set([0, 1]);
    (Array.isArray(ids) ? ids : []).forEach((id) => {
        const outfitId = parseInt(id, 10);
        if (Number.isNaN(outfitId) || outfitId < 0) return;
        existingOutfitIds.add(outfitId);
        selectedOutfits.add(outfitId);
    });
    updateOutfitsGrid();
    void updateCreditDisplay();
};

export const clearExistingCharacterOutfits = () => {
    existingOutfitIds = new Set([0, 1]);
    selectedOutfits = new Set([0, 1]);
    updateOutfitsGrid();
    void updateCreditDisplay();
};

const getRecruitT = (key, fallback) => {
    const lang = (window.getCurrentLanguage && window.getCurrentLanguage()) || 'en';
    return window.translations?.[lang]?.['create-character']?.[key]
        ?? window.translations?.en?.['create-character']?.[key]
        ?? fallback;
};

const getOutfitName = (outfitId) => {
    const fromData = availableOutfits.find((o) => parseInt(o.id, 10) === outfitId);
    if (fromData?.name) return fromData.name;

    const gridItem = document.querySelector(`.char-outfits-item[data-outfit-id="${outfitId}"]`);
    return gridItem?.getAttribute('data-outfit-name') || `Outfit ${outfitId}`;
};

const syncOutfitPromptOverridesFromDom = () => {
    const list = document.getElementById('char-outfit-prompts-list');
    if (!list) return;

    list.querySelectorAll('[data-outfit-prompt-id]').forEach((textarea) => {
        const outfitId = parseInt(textarea.getAttribute('data-outfit-prompt-id'), 10);
        if (Number.isNaN(outfitId)) return;
        outfitPromptOverrides.set(outfitId, textarea.value);
    });
    list.querySelectorAll('[data-outfit-negative-id]').forEach((textarea) => {
        const outfitId = parseInt(textarea.getAttribute('data-outfit-negative-id'), 10);
        if (Number.isNaN(outfitId)) return;
        outfitNegativeOverrides.set(outfitId, textarea.value);
    });
};

const updateOutfitPromptFields = () => {
    const advancedEl = document.getElementById('char-outfit-prompts-advanced');
    const listEl = document.getElementById('char-outfit-prompts-list');
    const tierNoticeEl = document.getElementById('char-outfit-prompts-tier-notice');
    if (!advancedEl || !listEl) return;

    syncOutfitPromptOverridesFromDom();

    const extraSelected = Array.from(selectedOutfits)
        .filter((id) => id > 1 && !isExistingOutfit(id))
        .sort((a, b) => a - b);

    advancedEl.classList.toggle('hidden', extraSelected.length === 0);
    if (extraSelected.length === 0) {
        listEl.innerHTML = '';
        return;
    }

    const { currentTier } = getUserTierInfo();
    const isFree = currentTier === 'free';
    if (tierNoticeEl) tierNoticeEl.classList.toggle('hidden', !isFree);

    const placeholder = getRecruitT(
        'outfit_prompts_placeholder',
        'use only english tags, e.g.: casual, white shirt, crop top, bra strap, dolphin shorts,'
    );

    listEl.innerHTML = '';
    extraSelected.forEach((outfitId) => {
        const outfitName = getOutfitName(outfitId);
        const row = document.createElement('div');
        row.className = 'char-outfit-prompt-row';

        const label = document.createElement('label');
        label.className = 'char-outfit-prompt-label';
        label.textContent = `${outfitName}:`;
        label.setAttribute('for', `outfit-prompt-${outfitId}`);

        const textarea = document.createElement('textarea');
        textarea.id = `outfit-prompt-${outfitId}`;
        textarea.className = 'form-input';
        textarea.setAttribute('data-outfit-prompt-id', String(outfitId));
        textarea.setAttribute('rows', '3');
        textarea.setAttribute('maxlength', '500');
        textarea.setAttribute('tabindex', '0');
        textarea.setAttribute('aria-label', `Custom prompt for ${outfitName}`);
        textarea.placeholder = placeholder;
        textarea.disabled = isFree;
        textarea.value = outfitPromptOverrides.get(outfitId) || '';

        textarea.addEventListener('input', () => {
            outfitPromptOverrides.set(outfitId, textarea.value);
        });

        const negativeLabel = document.createElement('label');
        negativeLabel.className = 'char-outfit-prompt-label';
        negativeLabel.textContent = getRecruitT('outfit_negative_label', 'Outfit negative prompt');
        negativeLabel.setAttribute('for', `outfit-negative-${outfitId}`);

        const negative = document.createElement('textarea');
        negative.id = `outfit-negative-${outfitId}`;
        negative.className = 'form-input';
        negative.setAttribute('data-outfit-negative-id', String(outfitId));
        negative.setAttribute('rows', '3');
        negative.setAttribute('maxlength', '500');
        negative.setAttribute('tabindex', '0');
        negative.setAttribute('aria-label', `Negative prompt for ${outfitName}`);
        negative.placeholder = getRecruitT('negative_prompt_placeholder', 'tags to avoid, e.g. bad hands, extra fingers,');
        negative.disabled = isFree;
        negative.value = outfitNegativeOverrides.get(outfitId) || '';
        negative.addEventListener('input', () => {
            outfitNegativeOverrides.set(outfitId, negative.value);
        });

        row.appendChild(label);
        row.appendChild(textarea);
        row.appendChild(negativeLabel);
        row.appendChild(negative);
        listEl.appendChild(row);

        if (!isFree) {
            initializeOutfitPromptAutocomplete(`outfit-prompt-${outfitId}`)
                .catch((e) => console.warn('init outfit prompt autocomplete failed', e));
            initializeNegativePromptAutocomplete(`outfit-negative-${outfitId}`)
                .catch((e) => console.warn('init outfit negative autocomplete failed', e));
        }
    });
};

// Load outfits data from CDN
const loadOutfitsData = async () => {
    if (availableOutfits.length > 0) return filterOutfitsForSfw(availableOutfits);

    try {
        const lang = (window.getCurrentLanguage && window.getCurrentLanguage()) || 'en';
        let outfits = await fetchCache.fetchJSON(catalogJsonUrl(`data/outfits/outfits-${lang}.json`));
        if (outfits && outfits.outfits && Array.isArray(outfits.outfits)) outfits = outfits.outfits;
        availableOutfits = outfits || [];
        window.outfitsData = outfits;
    } catch (error) {
        console.error('[CharacterOutfits] Error loading outfits data:', error);
        availableOutfits = [];
    }

    return filterOutfitsForSfw(availableOutfits);
};

const filterOutfitsByGender = (outfits, gender) => {
    if (!gender || !Array.isArray(outfits)) return outfits;

    const normalizedGender = gender.toLowerCase();

    return outfits.filter(outfit => {
        // Always include default outfits (IDs 0 and 1)
        if (parseInt(outfit.id) === 0 || parseInt(outfit.id) === 1) {
            return true;
        }

        if (outfit.gender) {
            const outfitGender = outfit.gender.toLowerCase();

            // If outfit gender matches selected gender, include it
            if (outfitGender === normalizedGender) {
                return true;
            } else {
                return false;
            }
        }

        return false;
    });
};

// Validate and clean selected outfits based on current gender
const validateSelectedOutfitsForGender = async (gender) => {
    try {
        const allOutfits = await loadOutfitsData();
        const filteredOutfits = filterOutfitsByGender(allOutfits, gender);
        const validOutfitIds = new Set(filteredOutfits.map(outfit => parseInt(outfit.id)));

        validOutfitIds.add(0);
        if (!outfitIsHiddenInFilteredMode({ id: 1, name: 'nude' })) {
            validOutfitIds.add(1);
        }

        // Remove selected outfits that are not valid for this gender
        const invalidSelections = Array.from(selectedOutfits).filter(id => !validOutfitIds.has(id));
        if (invalidSelections.length > 0) {
            invalidSelections.forEach(id => selectedOutfits.delete(id));
        }

        return true;
    } catch (error) {
        console.error('[CharacterOutfits] Error validating outfits for gender:', error);
        return false;
    }
};

// Get user tier and credit information
const getUserTierInfo = () => {
    const userData = storage.get('userData');
    const currentTier = perkTierForUser(userData || {});
    const spriteCredits = userData?.sprite_credits || 0;
    const canSelectAnyOutfit = !['free', 'trial_offer'].includes(currentTier);

    return { currentTier, spriteCredits, canSelectAnyOutfit };
};

// Calculate outfit selection costs locally from preloaded pricing config
const calculateOutfitCosts = async () => {
    const { spriteCredits } = getUserTierInfo();
    const allSelectedOutfits = Array.from(selectedOutfits).filter((id) => !isExistingOutfit(id));

    await loadCreditPricing();
    const { total, quantity, unitCost } = computeOutfitBatchTotal({
        outfitIds: allSelectedOutfits,
        characterHasLora: false,
        style: getCharacterFormStyle(),
    });
    const result = buildLocalCreditResult(total);
    const availableCredits = result.available_credits ?? spriteCredits;

    return {
        ok: true,
        totalCost: total,
        availableCredits,
        hasEnoughCredits: result.has_enough_credits ?? (total <= availableCredits),
        selectedOutfitsCount: allSelectedOutfits.length,
        totalSprites: quantity,
        creditsPerSprite: unitCost,
    };
};

const updateCreditDisplay = async () => {
    const creditInfo = document.getElementById('outfits-credit-info');
    if (!creditInfo) return;
    const { totalCost, availableCredits, hasEnoughCredits } = await calculateOutfitCosts();

    const text = creditInfo.querySelector('[data-translate="outfits_credit_usage"]') ||
        creditInfo.querySelector('span');
    if (text) {
        text.textContent = `This will use ${totalCost} / ${availableCredits} credits`;
    }
    creditInfo.classList.remove('is-hidden');
    creditInfo.classList.toggle('insufficient', !hasEnoughCredits);
};

const showTooltip = (text, event) => {
    const tooltipElement = document.getElementById('outfit-tooltip');
    if (!tooltipElement) return;
    tooltipElement.textContent = text;
    tooltipElement.style.left = `${event.pageX + 10}px`;
    tooltipElement.style.top = `${event.pageY - 10}px`;
    tooltipElement.classList.add('is-visible');
};

const hideTooltip = () => {
    document.getElementById('outfit-tooltip')?.classList.remove('is-visible');
};

// Create outfit item element
const createOutfitItem = (outfit, gender) => {
    const outfitItem = document.createElement('div');
    outfitItem.className = 'char-outfits-item';
    outfitItem.setAttribute('data-outfit-id', outfit.id);
    outfitItem.setAttribute('data-outfit-name', outfit.name);

    // Set initial selection state
    const isSelected = selectedOutfits.has(parseInt(outfit.id));
    outfitItem.classList.toggle('selected', isSelected);
    outfitItem.classList.toggle('unselected', !isSelected);

    Object.assign(outfitItem.style, {
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '2px solid transparent',
        transition: 'all 0.3s ease'
    });

    // Add hover tooltip functionality
    const outfitId = parseInt(outfit.id);
    const { canSelectAnyOutfit, currentTier } = getUserTierInfo();

    outfitItem.addEventListener('mouseenter', (event) => {
        if (outfitId === 0 || outfitId === 1 || isExistingOutfit(outfitId)) {
            showTooltip(isExistingOutfit(outfitId) && outfitId > 1
                ? 'Already added'
                : 'Cannot unselect default outfits', event);
        } else if (!canSelectAnyOutfit) {
            showTooltip('Upgrade membership to select more outfits', event);
        }
    });

    outfitItem.addEventListener('mouseleave', () => {
        hideTooltip();
    });

    outfitItem.addEventListener('mousemove', (event) => {
        const tooltipEl = document.getElementById('outfit-tooltip');
        if (tooltipEl?.classList.contains('is-visible')) {
            tooltipEl.style.left = `${event.pageX + 10}px`;
            tooltipEl.style.top = `${event.pageY - 10}px`;
        }
    });

    // Create outfit image
    const outfitImage = document.createElement('img');
    outfitImage.src = `${CDN_URL}/data/outfits/outfit_images/${gender}/${outfit.id}.webp`;
    outfitImage.alt = outfit.name;
    outfitImage.onerror = () => {
        // Fallback to placeholder
        outfitImage.src = '/assets/images/placeholder.svg';
    };

    outfitItem.appendChild(outfitImage);

    // Create outfit name label
    const outfitLabel = document.createElement('div');
    outfitLabel.className = 'char-outfits-label';
    outfitLabel.textContent = outfit.name;

    outfitItem.appendChild(outfitLabel);

    return outfitItem;
};

const handleOutfitSelection = async (outfitId) => {
    const { canSelectAnyOutfit } = getUserTierInfo();

    // IDs 0 and 1 cannot be deselected, nor can outfits already on the character
    if (outfitId === 0 || outfitId === 1 || isExistingOutfit(outfitId)) return;

    // For free/trial users, only allow selecting IDs 0 and 1
    if (!canSelectAnyOutfit && outfitId > 1) return;

    // Toggle selection
    if (selectedOutfits.has(outfitId)) {
        selectedOutfits.delete(outfitId);
    } else {
        selectedOutfits.add(outfitId);
    }

    updateOutfitsGrid();
    await updateCreditDisplay();
};

const updateOutfitsGrid = () => {
    const grid = document.getElementById('char-outfits-grid');
    if (!grid) return;

    const items = grid.querySelectorAll('.char-outfits-item');
    items.forEach(item => {
        const outfitId = parseInt(item.getAttribute('data-outfit-id'));
        const isSelected = selectedOutfits.has(outfitId);
        const isLocked = outfitId === 0 || outfitId === 1 || isExistingOutfit(outfitId);

        item.classList.toggle('selected', isSelected);
        item.classList.toggle('unselected', !isSelected);
        item.classList.toggle('char-outfits-item--locked', isLocked);
        item.setAttribute('data-existing', isLocked ? '1' : '0');
        item.setAttribute('aria-disabled', isLocked ? 'true' : 'false');

        const img = item.querySelector('img');
        if (img) {
            img.style.filter = isSelected ? 'none' : 'grayscale(100%)';
            img.style.opacity = isSelected ? '1' : '0.6';
        }
    });

    updateOutfitPromptFields();
};

// Initialize character outfits selection
const initializeCharacterOutfits = async () => {

    try {
        // Load outfits data
        const allOutfits = await loadOutfitsData();
        if (!allOutfits.length) {
            console.warn('[CharacterOutfits] No outfits data available');
            return;
        }

        // Get current gender from form
        const genderSelect = document.getElementById('ai-character-gender');
        const currentGender = genderSelect ? genderSelect.value || 'female' : 'female';

        // Filter outfits by gender
        let outfits = filterOutfitsByGender(allOutfits, currentGender);

        // Sort outfits by ID in ascending order
        outfits.sort((a, b) => {
            const aId = parseInt(a.id);
            const bId = parseInt(b.id);
            return aId - bId;
        });

        // Get outfits grid container
        const grid = document.getElementById('char-outfits-grid');
        if (!grid) {
            console.warn('[CharacterOutfits] Outfits grid container not found');
            return;
        }

        grid.innerHTML = '';

        // Create outfit items
        outfits.forEach(outfit => {
            const outfitItem = createOutfitItem(outfit, currentGender);

            outfitItem.addEventListener('click', () => {
                const outfitId = parseInt(outfit.id);
                handleOutfitSelection(outfitId);
            });

            grid.appendChild(outfitItem);
        });

        // Update visual selection state for all items (ensures defaults 0 and 1 show as selected)
        updateOutfitsGrid();

        await updateCreditDisplay();


    } catch (error) {
        console.error('[CharacterOutfits] Failed to initialize outfits:', error);
    }
};

// Get selected outfits for form submission
const getSelectedOutfits = () => {
    return Array.from(selectedOutfits).sort((a, b) => a - b);
};

export const buildStep3OutfitPrompts = (outfitIds) =>
    outfitIds.map((id) => (outfitPromptOverrides.get(id) || '').trim());

export const buildStep3OutfitNegatives = (outfitIds) =>
    outfitIds.map((id) => (outfitNegativeOverrides.get(id) || '').trim());


// ===== End Character Outfits Selection =====

const saveCharacterDraft = async () => {
    /** @type {HTMLFormElement} */
    const form = document.getElementById('character-form');
    if (!form) {
        return;
    }
    const data = new FormData(form);
    const plainObj = {};
    for (const [k, v] of data.entries()) plainObj[k] = v;

    // Save dropdown values that aren't captured by FormData
    try {
        // Handle voice select (could be custom dropdown or regular select)
        const voiceSelect = document.getElementById('ai-character-voice');
        if (voiceSelect) {
            if (typeof voiceSelect.getValue === 'function') {
                plainObj['ai-character-voice'] = voiceSelect.getValue();
            } else {
                plainObj['ai-character-voice'] = voiceSelect.value;
            }
        }

        // Save selected outfits
        plainObj.selectedOutfits = getSelectedOutfits();
        plainObj.outfitPromptOverrides = Object.fromEntries(outfitPromptOverrides);
        plainObj.outfitNegativeOverrides = Object.fromEntries(outfitNegativeOverrides);

        // Save step2 KV rows
        const kvTable = document.getElementById('step2-kv-table');
        if (kvTable) {
            plainObj['step2_rows'] = Array.from(kvTable.querySelectorAll('.step2-kv-row')).map((row) => ({
                key: row.querySelector('.kv-key')?.value || '',
                value: row.querySelector('.kv-value')?.value || '',
            }));
        }

        // Save additional fields
        const promptEl = $('#ai-character-prompt');
        const requestEl = $('#ai-request');
        plainObj['ai-character-prompt'] = promptEl ? val(promptEl) : '';
        plainObj['ai-request'] = requestEl ? val(requestEl) : '';
    } catch (e) {
        console.warn('[CharacterForm] Failed to capture dropdown data:', e);
    }

    // Note: Tag textareas are already captured by FormData above

    // preview image
    const prevUrl = getPreviewUrl();
    plainObj.__preview = await embedOrStore(await getBase64FromUrl(prevUrl));
    try {
        storage.setJSON(CHAR_DRAFT_KEY, plainObj);
    } catch (e) {
        console.warn('Failed to save character draft', e);
    }

    // If we are editing a scenario slot draft, also sync the latest preview URL + prompts back
    // so `create-scenario.html` can restore and autosave the updated preview consistently.
    if (scenarioDraftKey) {
        try {
            const slotDraft = storage.getJSON(scenarioDraftKey) || {};
            const slotNum = parseInt(String(slotParam || ''), 10) || 0;

            const kvPersonality = (() => {
                if (!Array.isArray(plainObj['step2_rows'])) return '';
                const row = plainObj['step2_rows'].find((r) => r.key && r.key.toLowerCase() === 'personality');
                return row ? row.value : '';
            })();
            const nextSlotDraft = {
                ...slotDraft,
                // Scenario draft shape uses these keys
                name: (plainObj['ai-character-name'] || slotDraft.name || '').toString(),
                personality: (kvPersonality || slotDraft.personality || slotDraft.goal || '').toString(),
                prompt: (plainObj['ai-character-prompt'] || slotDraft.prompt || '').toString(),
                outfit_prompt: (plainObj['ai-outfit-prompt'] || slotDraft.outfit_prompt || slotDraft.outfitPrompt || '').toString(),
                appearance_negative: (plainObj['ai-appearance-negative'] || slotDraft.appearance_negative || '').toString(),
                outfit_negative: (plainObj['ai-outfit-negative'] || slotDraft.outfit_negative || '').toString(),
                gender: (plainObj['ai-character-gender'] || slotDraft.gender || '').toString(),
                previewUrl: (prevUrl || slotDraft.previewUrl || '').toString()
            };

            storage.setJSON(scenarioDraftKey, nextSlotDraft);

            // If we have a slot number, also update the main scenario draft preview snapshot.
            // This is the source of truth used by `restoreDraft()` for slot previews.
            if (slotNum >= 1 && slotNum <= 3 && prevUrl) {
                try {
                    const mainDraft = storage.getJSON('scenario_draft_v2') || {};
                    if (!mainDraft.__previews) mainDraft.__previews = {};
                    if (!mainDraft.__previews.characters) mainDraft.__previews.characters = {};
                    mainDraft.__previews.characters[slotNum] = prevUrl;
                    storage.setJSON('scenario_draft_v2', mainDraft);
                } catch {}
            }
        } catch (e) {
            console.warn('[CharacterForm] Failed to sync scenario slot draft', e);
        }
    }
};

// Removed complex tag restoration - now using simple textarea values

const restoreCharacterDraft = async () => {
    const draft = storage.getJSON(CHAR_DRAFT_KEY);
    if (!draft) return;

    const form = document.getElementById('character-form');
    if (!form) {
        console.warn('[CharacterForm] No character form found in DOM');
        return;
    }

    Object.entries(draft).forEach(([k, v]) => {
        if (k.startsWith('__')) return;
        const el = form.elements[k];
        if (el) {
            el.value = k === 'ai-character-prompt' ? stripCharacterGenderTags(v) : v;
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });

    // Restore dropdown values that may not be captured by form.elements
    // Temporarily disable automatic prompt appending during restoration
    const wasAIRunning = window.isAIAssistantRunning;
    window.isAIAssistantRunning = true;

    try {
        // Handle voice select restoration (custom dropdown or regular select)
        if (draft['ai-character-voice']) {
            const voiceSelect = document.getElementById('ai-character-voice');
            if (voiceSelect) {
                if (typeof voiceSelect.setValue === 'function') {
                    voiceSelect.setValue(draft['ai-character-voice']);
                } else {
                    voiceSelect.value = draft['ai-character-voice'];
                }
                voiceSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    } catch (e) {
        console.warn('[CharacterForm] Failed to restore dropdown data:', e);
    } finally {
        // Restore the original AI assistant running state
        window.isAIAssistantRunning = wasAIRunning;
    }

    // Ensure gender-driven UI (body grid, outfits, prompt tags, voice lists on recruit page, etc.)
    // re-syncs after restoration. The generic "input" dispatch above doesn't trigger the jQuery
    // "change" handler used for gender.
    try {
        const restoredGender = draft['ai-character-gender'];
        const genderEl = /** @type {HTMLSelectElement|null} */ (form.elements['ai-character-gender'] || null);
        if (genderEl && restoredGender) {
            genderEl.value = restoredGender;
            genderEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
    } catch (e) {
        console.warn('[CharacterForm] Failed to re-sync gender after draft restore', e);
    }

    // Restore selected outfits
    if (draft.selectedOutfits && Array.isArray(draft.selectedOutfits)) {
        selectedOutfits = new Set(draft.selectedOutfits);
        selectedOutfits.add(0);
        selectedOutfits.add(1);
        existingOutfitIds.forEach((id) => selectedOutfits.add(id));

        // Validate restored outfits against current gender after outfits are loaded
        const genderEl = $('#ai-character-gender');
        const currentGender = (genderEl ? val(genderEl) : '') || 'female';
        validateSelectedOutfitsForGender(currentGender).then(async () => {
            // Update the UI after validation
            updateOutfitsGrid();
            await updateCreditDisplay();
        });
    }

    if (draft.outfitPromptOverrides && typeof draft.outfitPromptOverrides === 'object') {
        outfitPromptOverrides = new Map(
            Object.entries(draft.outfitPromptOverrides)
                .map(([key, value]) => [parseInt(key, 10), String(value ?? '')])
                .filter(([key]) => !Number.isNaN(key))
        );
    }
    if (draft.outfitNegativeOverrides && typeof draft.outfitNegativeOverrides === 'object') {
        outfitNegativeOverrides = new Map(
            Object.entries(draft.outfitNegativeOverrides)
                .map(([key, value]) => [parseInt(key, 10), String(value ?? '')])
                .filter(([key]) => !Number.isNaN(key))
        );
    }
    if (draft.outfitPromptOverrides || draft.outfitNegativeOverrides) {
        updateOutfitPromptFields();
    }

    // Restore step2 KV rows
    if (Array.isArray(draft.step2_rows)) {
        const kvTable = document.getElementById('step2-kv-table');
        if (kvTable) {
            releaseKvRows(kvTable);
            kvTable.innerHTML = '';
            draft.step2_rows.forEach(({ key, value }) => kvTable.appendChild(createKvRow(key, value)));
            refreshKvCounter();
        }
    }

    if (draft.__preview) {
        let url = draft.__preview;
        if (url.startsWith('db:')) {
            const data = await loadPreview(url.slice(3));
            if (data) url = data;
        }
        if (url) {
            const div = document.querySelector('#ai-character-prompt-preview .card-front');
            if (div) {
                div.querySelector('.preview-placeholder')?.classList.add('is-hidden');
                div.style.backgroundImage = `url(${url})`;
                div.style.backgroundSize = 'contain';
                div.style.backgroundPosition = 'center';
            }
        }
    }

    rememberCharacterName(document.getElementById('ai-character-name')?.value || '');
};

const setAssistantResetFieldValue = (id, nextValue) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.value = nextValue ?? '';
    el.dispatchEvent(new Event('input', { bubbles: true }));
};

export const resetCharacterFormForAssistantFill = () => {
    rememberCharacterName('');
    setAssistantResetFieldValue('ai-character-name', '');
    setAssistantResetFieldValue('ai-character-prompt', '');
    setAssistantResetFieldValue('ai-outfit-prompt', '');
    setAssistantResetFieldValue('ai-appearance-negative', '');
    setAssistantResetFieldValue('ai-outfit-negative', '');
    setAssistantResetFieldValue('lora-id-input', '');
    setAssistantResetFieldValue('lora-strength-input', '');

    const voiceSelect = document.getElementById('ai-character-voice');
    if (voiceSelect && typeof voiceSelect.setValue === 'function') {
        voiceSelect.setValue('');
    } else if (voiceSelect) {
        voiceSelect.value = '';
    }

    clearBodySelectionState();

    const kvTable = document.getElementById('step2-kv-table');
    if (kvTable) {
        releaseKvRows(kvTable);
        kvTable.innerHTML = '';
        initStep2KvTable();
    }

    const previewDiv = document.querySelector('#ai-character-prompt-preview .card-front');
    const previewContainer = document.getElementById('ai-character-prompt-preview');
    if (previewDiv) {
        previewDiv.style.backgroundImage = '';
        previewDiv.querySelector('.preview-placeholder')?.classList.remove('is-hidden');
    }
    if (previewContainer) {
        previewContainer.style.border = '1px dashed rgba(255, 255, 255, 0.3)';
    }

    const selectedTagsEl = $('.selected-tags');
    if (selectedTagsEl) html(selectedTagsEl, '');

    outfitPromptOverrides = new Map();
    outfitNegativeOverrides = new Map();
    clearExistingCharacterOutfits();
};

const clearCharacterDraft = () => {
    // Clear the local character draft only
    storage.remove(CHAR_DRAFT_KEY);
    rememberCharacterName('');

    // Reset form to default values
    const form = document.getElementById('character-form');
    if (form) {
        form.reset();

        // Reset visibility to public (default)
        $$('.visibility-btn').forEach(el => removeClass(el, 'active'));
        const publicBtn = $('#public-btn');
        if (publicBtn) addClass(publicBtn, 'active');

        // Clear character preview image
        const previewDiv = document.querySelector('#ai-character-prompt-preview .card-front');
        const previewContainer = document.getElementById('ai-character-prompt-preview');
        if (previewDiv) {
            previewDiv.style.backgroundImage = '';
            previewDiv.querySelector('.preview-placeholder')?.classList.remove('is-hidden');
        }
        // Ensure the parent container has the border
        if (previewContainer) {
            previewContainer.style.border = '1px dashed rgba(255, 255, 255, 0.3)';
        }

        // Clear selected tags
        const selectedTagsEl = $('.selected-tags');
        if (selectedTagsEl) html(selectedTagsEl, '');

        // Reset dropdowns to default values
        // Temporarily disable automatic prompt appending during reset
        const wasAIRunning = window.isAIAssistantRunning;
        window.isAIAssistantRunning = true;

        const genderResetEl = $('#ai-character-gender');
        if (genderResetEl) val(genderResetEl, 'female');
        const bustResetEl = $('#ai-character-bust');
        if (bustResetEl) val(bustResetEl, String(DEFAULT_BODY_OPTION_ID));

        // Reset voice select (handle custom dropdown)
        const voiceSelect = document.getElementById('ai-character-voice');
        if (voiceSelect && typeof voiceSelect.setValue === 'function') {
            voiceSelect.setValue('');
        } else {
            const voiceEl = $('#ai-character-voice');
            if (voiceEl) val(voiceEl, '');
        }

        // Restore the original AI assistant running state
        window.isAIAssistantRunning = wasAIRunning;

        // Clear AI assistant request
        const requestResetEl = $('#ai-request');
        if (requestResetEl) val(requestResetEl, '');
        const requestContainer = $('#ai-request-container');
        if (requestContainer) hide(requestContainer);

        // Clear character prompt
        const promptResetEl = $('#ai-character-prompt');
        if (promptResetEl) val(promptResetEl, '');

        // Reset step2 KV table to default rows
        const kvTableReset = document.getElementById('step2-kv-table');
        if (kvTableReset) {
            kvTableReset.innerHTML = '';
            initStep2KvTable();
        }

        // Trigger input events to update character counters
        $$('input, textarea, select').forEach(el => {
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

};

const openBodyTypePicker = () => {
    const genderSelect = document.getElementById('ai-character-gender');
    const gender = normalizeGender(genderSelect?.value || currentBodyGender);
    const hiddenInput = getBustInput();
    const selectedId = isBustInputEmpty(hiddenInput)
        ? null
        : resolveBodyOptionId(gender, hiddenInput.value);
    const items = getVisibleBodyOptions(gender).map((option) => ({
        id: option.id,
        label: option.label,
        imageUrl: getBodyImagePath(gender, option.id),
    }));
    openPromptImagePicker({
        title: getCharacterPromptWarningCopy('helper_body', 'Body'),
        showSearch: false,
        items,
        selectedId,
        onSelect: (item) => setBodySelectionState(item.id, { gender }),
    });
};

const debounce = (fn, delay = 500) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), delay); } };

const draftId = urlParams.get('draft');

export async function initializeCharacterForm() {
    if (window.__characterFormInitDone__) {
        return;
    }
    window.__characterFormInitDone__ = true;

    // Initialize body types dropdown based on current gender
    const genderSelectEl = $('#ai-character-gender');
    const initialGender = normalizeGender((genderSelectEl ? val(genderSelectEl) : '') || 'female');

    initializeBodyGrid(initialGender);
    updateAllPromptTags(initialGender);

    // Handle gender change to update body types
    if (genderSelectEl) {
        on(genderSelectEl, 'change', async function () {
            const selectedGender = normalizeGender(val(this) || 'female');

            initializeBodyGrid(selectedGender);
            updateAllPromptTags(selectedGender);
            refreshCharacterPromptWarnings();

            // Validate and clean selected outfits for new gender
            await validateSelectedOutfitsForGender(selectedGender);

            // Refresh outfits based on new gender
            await initializeCharacterOutfits();
        });
    }

    // Handle bust size changes - automatically update tags in character prompt
    const bustEl = $('#ai-character-bust');
    if (bustEl) {
        on(bustEl, 'input', function () {
            syncBodySelectionFromInput();
            updateBodyPromptTags();
        });
    }

    const promptEl = document.getElementById('ai-character-prompt');
    if (promptEl) {
        promptEl.addEventListener('input', refreshCharacterPromptWarnings);
    }
    document.addEventListener('languageSwitched', refreshCharacterPromptWarnings);
    initCharacterAppearanceHelpers({
        extraGroups: {
            body: {
                type: 'action',
                run: () => openBodyTypePicker(),
            },
        },
    });
    refreshCharacterPromptWarnings();

    // Set up character counters for text fields
    const textFields = [
        { id: 'ai-character-name', counter: 'ai-name-counter', maxLength: 35 },
        { id: 'ai-character-prompt', counter: 'ai-character-prompt-counter', maxLength: 500 },
        { id: 'ai-outfit-prompt', counter: 'ai-outfit-prompt-counter', maxLength: 250 }
    ];

    textFields.forEach(field => {
        if (document.getElementById(field.id)) {
            validation.setupCharacterCount(field.id, field.counter, field.maxLength);
        }
    });

    const nameEl = document.getElementById('ai-character-name');
    if (nameEl && nameEl.dataset.nameKvRewriteBound !== '1') {
        nameEl.dataset.nameKvRewriteBound = '1';
        nameEl.addEventListener('change', () => {
            commitCharacterNameChange(nameEl.value);
        });
    }

    // Handle visibility toggle
    $$('.visibility-btn').forEach(el => {
        on(el, 'click', function () {
            $$('.visibility-btn').forEach(btn => removeClass(btn, 'active'));
            addClass(this, 'active');
        });
    });

    // Handle clear draft button
    const clearDraftBtn = $('#clear-draft-btn');
    if (clearDraftBtn) on(clearDraftBtn, 'click', () => clearCharacterDraft());

    // Restore draft from scenario if exists (slot-based)
    const draft = scenarioDraftKey ? storage.getJSON(scenarioDraftKey) : null;
    if (draft) {
        const set = (id, val) => { const el = document.getElementById(id); if (el) { el.value = val || ''; el.dispatchEvent(new Event('input', { bubbles: true })); } };
        set('ai-character-name', draft.name);
        rememberCharacterName(draft.name);
        // Prefer high-level personality/goal description if available, otherwise fall back to tag-based description
        const personalityDesc = draft.personality || draft.goal || '';
        // Populate Personality KV row directly (description textarea no longer exists)
        if (personalityDesc || draft.description) {
            const kvTable = document.getElementById('step2-kv-table');
            if (kvTable) {
                const personalityRow = Array.from(kvTable.querySelectorAll('.step2-kv-row')).find(
                    (r) => (r.querySelector('.kv-key')?.value || '').toLowerCase() === 'personality'
                );
                const valueToSet = personalityDesc || draft.description || '';
                if (personalityRow) {
                    const valEl = personalityRow.querySelector('.kv-value');
                    if (valEl instanceof HTMLTextAreaElement) setKvValue(valEl, valueToSet);
                }
            }
        }

        // Also pre-fill the AI assistant request textarea so users can immediately refine the character using the rich description
        const aiRequestEl = document.getElementById('ai-request');
        if (aiRequestEl && !aiRequestEl.value) {
            aiRequestEl.value = personalityDesc || draft.description || '';
        }

        // Only set prompt from draft if the field is empty (to avoid overwriting AI-generated content)
        const promptEl = document.getElementById('ai-character-prompt');
        if (promptEl && !promptEl.value.trim()) {
            set('ai-character-prompt', stripCharacterGenderTags(draft.prompt || ''));
        }
        set('ai-outfit-prompt', draft.outfit_prompt || draft.outfitPrompt || '');
        set('ai-appearance-negative', draft.appearance_negative || '');
        set('ai-outfit-negative', draft.outfit_negative || '');
        set('ai-character-voice', draft.voiceType || '');

        // preview image
        if (draft.previewUrl) {
            const prev = document.querySelector('#ai-character-prompt-preview .card-front');
            if (prev) {
                prev.querySelector('.preview-placeholder')?.classList.add('is-hidden');
                prev.style.backgroundImage = `url(${draft.previewUrl})`;
                prev.style.backgroundSize = 'contain';
                prev.style.backgroundPosition = 'center top';
                prev.style.backgroundRepeat = 'no-repeat';
            }
        }

        // Set gender select if available
        if (draft.gender) {
            const genderSelect = document.getElementById('ai-character-gender');
            if (genderSelect) {
                genderSelect.value = draft.gender;
                genderSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        // Auto-run AI assistant to enrich other fields; image generation is skipped inside assistant when preview already exists
        // Auto-run AI assistant only if the draft is missing critical fields (i.e., not enriched yet)
        const needsEnrichment = !draft.age || !draft.height;
        if (needsEnrichment) {
            setTimeout(() => {
                try {
                    const container = document.getElementById('ai-request-container');
                    if (container) container.style.display = 'block';
                    const req = document.getElementById('ai-request');
                    if (req && !req.value) {
                        req.value = draft.description || draft.prompt || draft.name;
                    }
                    // Auto-run AI assistant ONLY if we are still on the character page (avoid leaking click to scenario page)
                    const aiSubmit = document.getElementById('submit-ai-request');
                    const isCharacterPage = document.getElementById('character-panel') && window.location.pathname.includes('create-character');
                    if (isCharacterPage && aiSubmit) aiSubmit.click();
                } catch (e) { console.warn('Auto AI generation failed', e); }
            }, 800);
        }
        // Do not remove scenarioDraftKey here; keep it so the scenario page can restore latest preview after character creation
    }

    // Handle return to scenario after character creation
    // Local draft restore if applicable
    if (!draftId && !draft) {
        try { await restoreCharacterDraft(); } catch (e) { console.warn('restoreCharacterDraft failed', e); }
    } else {
    }

    // Set up auto-save AFTER all restoration is complete
    const formEl = document.getElementById('character-form');
    if (formEl) {
        const debouncedSave = debounce(saveCharacterDraft, 500);
        formEl.addEventListener('input', debouncedSave);
        formEl.addEventListener('change', debouncedSave);
        // Save initial state after restoration
        await saveCharacterDraft();
    }

    // Initialize character outfits selection
    await initializeCharacterOutfits();

    document.addEventListener('languageSwitched', () => {
        availableOutfits = [];
        initializeCharacterOutfits();
    });

    // Sync sprite credits from backend
    await syncSpriteCredits();

    window.handleCharacterCreationSuccess = () => {
        const returnUrl = storage.get('scenario_return_url');
        if (returnUrl) {
            storage.remove('scenario_return_url');
            setTimeout(() => {
                window.location.href = returnUrl;
            }, 1500); // Small delay to show success message
        }
    };
}

/**
 * Handles successful character creation when coming from a scenario page
 */
async function handleScenarioReturn(slotParam, returnUrl, newId, charName, storage) {
    // Directly update main scenario draft with final character preview
    try {
        const finalPreviewUrl = getPreviewUrl();
        if (finalPreviewUrl && slotParam) {
            const slotNum = parseInt(slotParam);
            if (slotNum >= 1 && slotNum <= 3) {
                // Get main scenario draft
                const mainDraft = storage.getJSON('scenario_draft_v2') || {};

                // Ensure preview structure exists
                if (!mainDraft.__previews) mainDraft.__previews = {};
                if (!mainDraft.__previews.characters) mainDraft.__previews.characters = {};


                // Convert preview to Base64 and store appropriately
                let previewData = finalPreviewUrl;
                if (finalPreviewUrl.startsWith('http')) {
                    previewData = await getBase64FromUrl(finalPreviewUrl);
                }

                // Store in IndexedDB if it's a large Base64, otherwise store directly
                if (previewData.startsWith('data:') && previewData.length > 100_000) {
                    // Clean up old preview before storing new one
                    const oldPreviewRef = mainDraft.__previews.characters[slotNum];
                    const previewId = await cleanupAndSavePreview(previewData, oldPreviewRef);
                    mainDraft.__previews.characters[slotNum] = `db:${previewId}`;
                } else {
                    // Clean up old preview if it was stored in IndexedDB
                    const oldPreviewRef = mainDraft.__previews.characters[slotNum];
                    if (oldPreviewRef && oldPreviewRef.startsWith('db:')) {
                        const oldId = oldPreviewRef.slice(3);
                        await deletePreview(oldId);
                    }
                    mainDraft.__previews.characters[slotNum] = previewData;
                }

                // Save updated main draft
                storage.setJSON('scenario_draft_v2', mainDraft);

                // Clear conflicting caches for this slot (may have been set by old draft code)
                storage.remove(`character_draft_from_scenario_slot${slotNum}`);
            }
        }
    } catch (e) {
        console.warn('[CharacterForm] Failed to update main scenario draft preview:', e);
    }

    // Build safe absolute URL to avoid duplicate '?' and ensure proper navigation
    let redirectUrl = returnUrl;
    const hasQuery = redirectUrl.includes('?');
    redirectUrl += (hasQuery ? '&' : '?') + `slot=${slotParam}`;


    // Clean up draft + return marker BEFORE navigating
    storage.remove('scenario_return_url');
    // Keep scenarioDraftKey until scenario page consumes it to update preview

    window._redirectingToScenario = true; // flag for finally block
    // Use replace() to avoid leaving the user on character page when the history back button is pressed
    window.location.replace(new URL(redirectUrl, window.location.origin).toString());
}

/**
 * Handles successful character creation when not coming from a scenario page
 */
function handleRegularCreation() {
    // Preserve the AI-generated prompt before clearing the form
    const promptEl = document.getElementById('ai-character-prompt');
    const preservedPrompt = promptEl ? promptEl.value : '';
    clearCharacterDraft(); // Clear form fields after successful creation
    // Restore the AI-generated prompt
    if (promptEl && preservedPrompt) {
        promptEl.value = preservedPrompt;
    }
    showToast('Character created successfully!', 'check-circle');

    setTimeout(() => {
        window.location.href = './';
    }, 3000);
}

if (typeof window !== 'undefined') {
    window.addEventListener('DOMContentLoaded', () => {
        // Listen for sprite credits updates from server
        document.addEventListener('spriteCreditsUpdated', () => {
            updateCreditDisplay();
        });
    });
} 
